import { Box } from '@mui/material';
import { t } from 'i18next';
import { BaseController, BaseState } from '../../controllers/BaseController';
import { Severity } from '../../enums/Severity';
import { IssuesTable } from '../components/IssuesTable';
import { LoadMore } from '../components/LoadMore';
import { BlankIssues } from '../components/blankSlates/BlankIssues';
import { IssuesTable as SkeletonIssuesTable } from '../components/skeletons/IssuesTable';
import { LoadMore as SkeletonLoadMore } from '../components/skeletons/LoadMore';
import { Issue } from '../models/Issue';
import { Project } from '../models/Project';
import { IssuesRepository } from '../services/IssuesRepository';

type Props = {
  project: Project;
  severities: Severity[];
  withFilteredView: boolean;
  focusOnIssueNumber?: number | null;
};

type State = BaseState & {
  initialLoad: boolean; // indicates initial load so that we can display skeleton loader
  loadingData: boolean; // indicates that we are fetching data so that inline loaders can be set up
  cursor: string | null; // string value for the infinite scroll pagination
  hasNextPage: boolean;
  totalItems: number | null; // total items available for the page, used for pagination element
};

const DEFAULT_PER_PAGE = 5;

export class ProjectDetailsController extends BaseController<Props, State> {
  private readonly repository: IssuesRepository;

  /**
   * List of issues to display.
   * Not in state on purpose to trim down the number of internal checks between state changes.
   */
  private data: Issue[];

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.repository = new IssuesRepository();

    this.data = [];
  }

  getBaseState(): State {
    return { ...super.getBaseState(), initialLoad: true, loadingData: true, cursor: null, hasNextPage: true, totalItems: null };
  }

  componentDidMount() {
    super.componentDidMount();

    this.fetchData('set');
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    super.componentDidUpdate(prevProps, prevState);

    const severitiesHaveChanged = prevProps.severities.join(',') !== this.props.severities.join(',');
    const filteredViewHasChanged = prevProps.withFilteredView !== this.props.withFilteredView;

    if (severitiesHaveChanged || filteredViewHasChanged) {
      // Reset the whole state
      this.data = [];
      this.setState(
        {
          initialLoad: true,
          cursor: null,
          hasNextPage: true,
          totalItems: null,
        },
        () => this.fetchData('set')
      );
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  /**
   * Helper method to fetch data according to current state.
   * Side effects of the method:
   *  - initial load is marked off
   *  - data loading is first marked on, then off
   *  - cursor updated
   *  - hasNextPage updated
   *  - totalItems updated
   *
   */
  async fetchData(behavior: 'set' | 'append') {
    try {
      this.setState({ loadingData: true });

      const { data, cursor, hasNextPage, totalItems } = await this.repository.getList(
        this.props.project.getId(),
        this.state.cursor,
        DEFAULT_PER_PAGE,
        this.props.severities,
        this.props.withFilteredView
      );

      if (behavior === 'set') {
        this.data = data;
      } else {
        this.data.push(...data);
      }

      this.setState({ loadingData: false, initialLoad: false, cursor, hasNextPage, totalItems });
    } catch (e: unknown) {
      // stop loading, but leave the existing data rendered
      this.setState({ loadingData: false, initialLoad: false });

      this.handleError(e, { invalidUrl: t('nonExistingProject') });
    }
  }

  render() {
    const { totalItems } = this.state;

    let loadMoreLabel = t('showMoreInstancesDefault');
    if (totalItems !== null) {
      const remainingItems = totalItems - this.data.length;
      loadMoreLabel = t('showMoreInstances', { count: remainingItems });
    }

    const defaultOpenIssue = this.data.find((issue) => issue.getNumber() === this.props.focusOnIssueNumber) || null;

    return this.renderBase(
      <Box>
        {this.state.initialLoad ? (
          <>
            <SkeletonIssuesTable />
            <SkeletonLoadMore />
          </>
        ) : !this.state.loadingData && this.data.length === 0 ? (
          <BlankIssues withFilteredView={this.props.withFilteredView} />
        ) : (
          <>
            <IssuesTable data={this.data} defaultOpenIssue={defaultOpenIssue} />
            {this.state.hasNextPage && <LoadMore loading={this.state.loadingData} onLoadMore={() => this.fetchData('append')} label={loadMoreLabel} />}
          </>
        )}
      </Box>
    );
  }
}
